'use client';

import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Button,
  ModalBody,
  ModalHeader,
} from 'reactstrap';
import { isEmpty } from 'lodash';

import Loader from 'src/components/shared/loader';

import { generateRandomString } from 'src/helpers/utils';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { updateVisibility } from 'src/redux/modals/slice';
import { getPausedNotes } from 'src/redux/open-jobs/api';

interface PauseNoteItem {
  label?: string;
  value?: string;
  type?: string;
  [key: string]: unknown;
}

interface JobMediaItem {
  job_media_id?: string;
  job_media_thumbnail_url?: string;
  media_type?: string;
  [key: string]: unknown;
}

interface PausedNotesData {
  pause_note?: PauseNoteItem[];
  job_media?: JobMediaItem[];
  [key: string]: unknown;
}

const PausedNotesModal = () => {
  const { extraProps } = useAppSelector((state: Record<string, unknown>) => {
    const modals = state.modals as Record<string, unknown>;
    return modals.pausedNotesModal as Record<string, unknown>;
  });
  const [pausedNotes, setPausedNotes] = useState<PausedNotesData | undefined>();

  const [isPauseNotesFetching, setIsPauseNotesFetching] = useState(false);
  const dispatch = useAppDispatch();

  const ep = extraProps as Record<string, unknown> | undefined;
  const epData = ep?.data as Record<string, unknown> | undefined;

  useEffect(() => {
    setIsPauseNotesFetching(true);
    dispatch(getPausedNotes(epData?.jobId as string)({})).unwrap().then((res: unknown) => {
      setPausedNotes(res as PausedNotesData);
      setIsPauseNotesFetching(false);
    }).catch(() => {
      setIsPauseNotesFetching(false);
    });
  }, [dispatch, epData?.jobId]);

  const onImageClickHandler = (images: JobMediaItem[], img: JobMediaItem) => {
    const imageArrayForSlider = images.filter((image) => image !== img);
    imageArrayForSlider.unshift(img);

    dispatch(
      updateVisibility({
        modalType: 'imageSlidesModal',
        isVisible: true,
        extraProps: {
          modalData: {
            collection: imageArrayForSlider,
            showDeleteButton: false,
          },
        },
      }),
    );
  };
  const handleModalClose = useCallback(() => {
    dispatch(
      updateVisibility({
        modalType: 'pausedNotesModal',
        isVisible: false,
      }),
    );
  }, [dispatch]);

  return (
    <Modal
      isOpen
      toggle={handleModalClose}
      centered
      className="global-modal notes-modal paused-notes-modal"
    >
      <ModalHeader toggle={handleModalClose}>
        Pause Notes
        &nbsp;
      </ModalHeader>
      <ModalBody>
        <div className="divider" />
        <div className="pause-note-data-container">
          {isPauseNotesFetching ? <Loader /> : ''}
          {!isPauseNotesFetching && isEmpty(pausedNotes?.pause_note) ? (
            <div className="no-data-found">
              No Data Found
            </div>
          ) : ''}

          {!isEmpty(pausedNotes) ? (
            <React.Fragment>
              {pausedNotes?.job_media ? (
                <div className="paused-notes-modal__image-wrap">
                  {(pausedNotes?.job_media || [])?.map((image) => (
                    <div
                      className={[
                        'paused-notes-modal__image',
                        image?.media_type === 'video'
                          ? 'video' : '',
                      ].join(' ')}
                      key={image?.job_media_id}
                    >
                      <img
                        src={image?.job_media_thumbnail_url}
                        alt="job media"
                        key={(epData?.job as Record<string, unknown>)?.job_media_id as string}
                        width="70px"
                        height="70px"
                        onClick={() => onImageClickHandler(pausedNotes?.job_media || [], image)}
                      />
                    </div>
                  )) }
                </div>
              ) : ''}

              {(pausedNotes?.pause_note || [])
                ?.filter((pauseData) => !pauseData?.type)
                ?.map((pauseData, index) => (
                  <React.Fragment key={generateRandomString()}>
                    <div className="pause-note-section">
                      <div className="notes-heading">
                        {pauseData?.label}
                        {' '}
                        :
                      </div>
                      <p className="notes-heading__title">{pauseData?.value}</p>
                    </div>
                    {Number(
                      (pausedNotes?.pause_note
                        || [])?.length || 0,
                    ) - 1 !== index
                      ? <hr className="hr-md" />
                      : '' }
                  </React.Fragment>
                ))}
            </React.Fragment>
          ) : ''}
        </div>
        <div className="paused-notes-modal__button">
          <Button
            color="secondary"
            className={[
              'btn-block',
              'justify-content-center w-100',
            ].join(' ')}
            onClick={handleModalClose}
          >
            Close
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default PausedNotesModal;
